import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbSeparator,
    BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { HEADER_HEIGHT, MAIN_MARGIN } from "./AdminLayout";
import { NavLink, useLocation } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import NotificationDialog from "./NotificationDialog";

function isUUID(segment: string) {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
        segment,
    );
}

function formatSegment(segment: string) {
    return segment
        .split("-")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
}

export default function AdminHeader() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isUpdate = searchParams.get("isUpdate") === "true";

    // Split the path into usable segments
    let segments = location.pathname.split("/").filter(Boolean);

    // the root crumb already represents /dashboard
    if (segments[0] === "dashboard") {
        segments = segments.slice(1);
    }

    // --- special case for /time-sheet route ---
    if (segments[0] === "time-sheet") {
        if (isUpdate) {
            // /time-sheet/... + isUpdate=true → time-sheet/logs/view
            segments = ["time-sheet", "logs", "view"];
        } else {
            // /time-sheet/... (no isUpdate) → time-sheet/generate/view
            segments = ["time-sheet", "generate", "view"];
        }
    }

    return (
        <header
            className="flex shrink-0 items-center justify-between gap-2"
            style={{ height: HEADER_HEIGHT, marginRight: MAIN_MARGIN }}
        >
            <div className="flex items-center gap-2 px-4">
                <SidebarTrigger className="-ml-1" />
                <Separator
                    orientation="vertical"
                    className="mr-2 data-[orientation=vertical]:h-4"
                />

                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            {segments.length === 0 ? (
                                <BreadcrumbPage>Dashboard</BreadcrumbPage>
                            ) : (
                                <BreadcrumbLink asChild>
                                    <NavLink to="/dashboard">Dashboard</NavLink>
                                </BreadcrumbLink>
                            )}
                        </BreadcrumbItem>

                        {segments.map((seg, idx) => {
                            const prevSegment = segments[idx - 1];
                            const isCurrentUUID = isUUID(seg);
                            const path =
                                "/" + segments.slice(0, idx + 1).join("/");
                            const isLast = idx === segments.length - 1;

                            // --- UUID handling ---
                            if (isCurrentUUID && prevSegment === "edit") {
                                return null;
                            }

                            if (isCurrentUUID) {
                                return (
                                    <Fragment key={`uuid-${idx}`}>
                                        <BreadcrumbSeparator />
                                        <BreadcrumbItem>
                                            {isLast ? (
                                                <BreadcrumbPage>
                                                    View
                                                </BreadcrumbPage>
                                            ) : (
                                                <BreadcrumbLink asChild>
                                                    <NavLink to={path}>
                                                        View
                                                    </NavLink>
                                                </BreadcrumbLink>
                                            )}
                                        </BreadcrumbItem>
                                    </Fragment>
                                );
                            }

                            // --- Normal segment ---
                            const label = formatSegment(seg);
                            const isEdit = seg === "edit";

                            return (
                                <Fragment key={`seg-${idx}`}>
                                    <BreadcrumbSeparator />
                                    <BreadcrumbItem>
                                        {isLast || isEdit ? (
                                            <BreadcrumbPage>
                                                {label}
                                            </BreadcrumbPage>
                                        ) : (
                                            <BreadcrumbLink asChild>
                                                <NavLink to={path}>
                                                    {label}
                                                </NavLink>
                                            </BreadcrumbLink>
                                        )}
                                    </BreadcrumbItem>
                                </Fragment>
                            );
                        })}
                    </BreadcrumbList>
                </Breadcrumb>
            </div>
            <div className="">
                <NotificationDialog />
            </div>
        </header>
    );
}
